import { Card, CardContent } from "../../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { Users } from "lucide-react";
import { computeLotEconomics, weightedDaysXirr } from "./subCategoryClassifier";

/**
 * Section E — Family Member Wise Allocation table (Clubbed View only).
 * Per-member: Invested / Current / Abs Ret% / XIRR% / Alloc% (Total 100%).
 * Only renders when `familyAggregate && familyContext.is_group_head` and
 * the group has more than one family member.
 */
export const FamilyAllocationCard = ({
  filteredActiveUnits = [],
  familyContext,
  familyAggregate,
  panToClientName = {},
}) => {
  if (!familyAggregate || !familyContext?.is_group_head) return null;
  const members = familyContext?.family_members || [];
  if (members.length <= 1) return null;

  const byPan = {};
  filteredActiveUnits.forEach((txn) => {
    const pan = (txn._family_pan || txn.pan || txn.first_holder_pan || "").toUpperCase();
    if (!pan) return;
    if (!byPan[pan]) byPan[pan] = { cost: 0, market: 0, cashflows: [] };
    const { cost, market, dateObj } = computeLotEconomics(txn);
    byPan[pan].cost += cost;
    byPan[pan].market += market;
    if (cost > 0 && dateObj) byPan[pan].cashflows.push({ date: dateObj, amount: -cost });
  });

  const totalMarket = Object.values(byPan).reduce((s, b) => s + b.market, 0);
  const totalCost = Object.values(byPan).reduce((s, b) => s + b.cost, 0);

  const rows = members
    .map((m) => {
      const pan = (m.pan || "").toUpperCase();
      const b = byPan[pan] || { cost: 0, market: 0, cashflows: [] };
      const absRet = b.cost > 0 ? ((b.market - b.cost) / b.cost) * 100 : 0;
      const xirr = weightedDaysXirr(b.cost, b.market, b.cashflows);
      const alloc = totalMarket > 0 ? (b.market / totalMarket) * 100 : 0;
      return { pan, name: panToClientName[pan] || m.name || pan, cost: b.cost, market: b.market, absRet, xirr, alloc };
    })
    .filter((r) => r.cost > 0 || r.market > 0);

  return (
    <Card className="border-gray-200" data-testid="summary-family-allocation-card">
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-teal-600" />
          FAMILY MEMBER WISE ALLOCATION
        </h3>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Family member name</TableHead>
                <TableHead className="font-semibold text-right">Amount Invested</TableHead>
                <TableHead className="font-semibold text-right">Current value</TableHead>
                <TableHead className="font-semibold text-right">Abs Ret%</TableHead>
                <TableHead className="font-semibold text-right">XIRR%</TableHead>
                <TableHead className="font-semibold text-right">Alloc%</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-gray-400 py-6">
                    No allocations across family members
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {rows.map((r) => (
                    <TableRow key={r.pan} className="hover:bg-gray-50" data-testid={`family-alloc-row-${r.pan}`}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="text-right font-mono">
                        ₹{r.cost.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        ₹{r.market.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell className={`text-right font-mono ${r.absRet >= 0 ? "text-green-600" : "text-red-600"}`}>
                        {r.absRet >= 0 ? "+" : ""}
                        {r.absRet.toFixed(2)}%
                      </TableCell>
                      <TableCell className={`text-right font-mono ${r.xirr >= 0 ? "text-green-600" : "text-red-600"}`}>
                        {r.xirr >= 0 ? "+" : ""}
                        {r.xirr.toFixed(2)}%
                      </TableCell>
                      <TableCell className="text-right font-mono">{r.alloc.toFixed(2)}%</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-gray-100 font-semibold border-t-2">
                    <TableCell className="font-bold">Total</TableCell>
                    <TableCell className="text-right font-mono font-bold">
                      ₹{totalCost.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold">
                      ₹{totalMarket.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                    </TableCell>
                    <TableCell className={`text-right font-mono font-bold ${(totalMarket - totalCost) >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {totalCost > 0
                        ? `${((totalMarket - totalCost) / totalCost) * 100 >= 0 ? "+" : ""}${(((totalMarket - totalCost) / totalCost) * 100).toFixed(2)}%`
                        : "0.00%"}
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold">-</TableCell>
                    <TableCell className="text-right font-mono font-bold">100.00%</TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
